import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import register from "preact-custom-element";
import { useClickOrDrag } from "./useClickOrDrag";


const BetterZoom = ({ maxzoom = 1.5 }) => {
  const slotRef = useRef<HTMLSlotElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [moved, setMoved] = useState({ x: 0, y: 0 });
  const isScaled = useMemo(() => scale > 1, [scale]);
  useEffect(() => {
    if (isScaled) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isScaled]);

  const startX = useRef(0);
  const startY = useRef(0);
  const handlers = useClickOrDrag({
    onClick: () => {
      if (!itemRef.current) return;
      const rect = itemRef.current.getBoundingClientRect();
      const x = -(rect.x + rect.width / 2) + window.innerWidth / 2;
      const y = -(rect.y + rect.height / 2) + window.innerHeight / 2;
      setCenter(scale === 1 ? { x, y } : { x: 0, y: 0 });
      setScale(scale === 1 ? maxzoom : 1);
      setMoved({ x: 0, y: 0 });
    },
    onStart: (e) => {
      if (!isScaled) return;
      startX.current = e.screenX - moved.x;
      startY.current = e.screenY - moved.y;
    },
    onMove: (e) => {
      if (!isScaled) return;
      const dx = e.screenX - startX.current;
      const dy = e.screenY - startY.current;
      setMoved({ x: dx, y: dy });
    },

    onDrag: (e) => {
      if (!isScaled) return;
      if (!itemRef.current) return;
      const rect = itemRef.current.getBoundingClientRect();
      const padding = 10;
      const newMoved = { x: moved.x, y: moved.y };
      if (rect.width < window.innerWidth - (padding * 2)) {
        newMoved.x = 0;
      } else if (rect.x > padding) {
        newMoved.x = rect.width / 2 - window.innerWidth / 2 + padding;
      } else if (rect.x + rect.width < window.innerWidth - padding) {
        newMoved.x = window.innerWidth / 2 - rect.width / 2 - padding;
      }
      if (rect.height < window.innerHeight - (padding * 2)) {
        newMoved.y = 0;
      } else if (rect.y > padding) {
        newMoved.y = rect.height / 2 - window.innerHeight / 2 + padding;
      } else if (rect.y + rect.height < window.innerHeight - padding) {
        newMoved.y = window.innerHeight / 2 - rect.height / 2 - padding;
      }
      setMoved(newMoved);
    },
  });

  const style = {
    transform: `translate(${center.x}px, ${center.y}px) translate(${moved.x}px, ${moved.y}px) scale(${scale})`,
  };

  // console.log(itemRef.current?.getBoundingClientRect());
  return <>
    <div style={style} ref={itemRef} {...handlers} className={"zoomable" + (isScaled ? " scaled" : "")}>
      <slot ref={slotRef} />
    </div>
    {isScaled &&
      <div class="overlay" {...handlers}> </div>
    }
    <style>
      {`
      .zoomable {
        position: relative;
        transition: transform 0.1s ease;
        display: inline-block;
        cursor: pointer;
        user-select: none;
      }
      .scaled {
        z-index: 100;
        pointer-events: none;
      }
      .overlay {
        content: "";
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: all;
        touch-action: none; // Prevent touch events from being interpreted as scroll events
        overscroll-behavior: contain; // Prevent scroll chaining, not reaaaally necessary
        z-index: 1000;
        background: radial-gradient(
          ellipse at center,
          rgba(0, 0, 0, 0) 45%,
          rgba(0, 0, 0, .3) 100%
        );
      }
      `}
    </style>
  </>;
};

register(BetterZoom, "better-zoom", ["maxzoom"], { shadow: true });
